/**
 * The 7 warehouse process types as `OptionGridSelector` cards (icon + label +
 * description) for the create wizard's first step. Also encodes which endpoint
 * kinds each type moves between (drives the source/destination selectors).
 */
import type { ReactNode } from 'react';
import {
  ArrowLeftRight,
  PackageCheck,
  PackagePlus,
  ClipboardCheck,
  Truck,
  Undo2,
  HandCoins,
} from 'lucide-react';
import type { OptionGridItem } from '@vnetwork/ui/primitives';
import type { ProcessType } from '@/api/capacities';
import { PROCESS_TYPE_DESCRIPTION, PROCESS_TYPE_LABEL } from '../process-format';

/** Endpoint source used to pick which dropdown feeds source/destination. */
export type EndpointKind = 'supplier' | 'location' | 'customer';

/** For each type: what the source and destination endpoints are. */
export const PROCESS_ENDPOINT_MAP: Record<
  ProcessType,
  { source?: EndpointKind; destination?: EndpointKind }
> = {
  import_purchase: { source: 'supplier', destination: 'location' },
  import_return: { source: 'customer', destination: 'location' },
  export_sale: { source: 'location', destination: 'customer' },
  export_loan: { source: 'location', destination: 'customer' },
  export_delivery: { source: 'location', destination: 'location' },
  internal_transfer: { source: 'location', destination: 'location' },
  inventory_check: { source: 'location' },
};

const ICONS: Record<ProcessType, ReactNode> = {
  import_purchase: <PackagePlus size={20} />,
  import_return: <Undo2 size={20} />,
  export_sale: <PackageCheck size={20} />,
  export_loan: <HandCoins size={20} />,
  export_delivery: <Truck size={20} />,
  internal_transfer: <ArrowLeftRight size={20} />,
  inventory_check: <ClipboardCheck size={20} />,
};

export const PROCESS_TYPE_OPTIONS: OptionGridItem<ProcessType>[] = (
  Object.keys(PROCESS_TYPE_LABEL) as ProcessType[]
).map((type) => ({
  value: type,
  title: PROCESS_TYPE_LABEL[type],
  description: PROCESS_TYPE_DESCRIPTION[type],
  icon: ICONS[type],
}));
